/**
 * Library Post-Call Actions — define the analysis checks that run
 * after every call. Each action has an LLM prompt that produces
 * structured JSON analysis. Tenants inherit these actions and can
 * enable/disable or customize per sub-tenant.
 */

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import * as api from "../api.js";
import { PageHeader, Loading, ErrorBanner } from "../components.jsx";
import { useLibraryEnv } from "../LibraryEnvContext.jsx";

export default function LibraryPostCallActions() {
  const { env, isDraft, isLive } = useLibraryEnv();
  const nav = useNavigate();
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.listPostCallActions(env);
      setActions(res.data || []);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [env]);

  useEffect(() => { load(); }, [load]);

  const saveAction = async () => {
    if (!editing) return;
    setBusy(true);
    setError(null);
    try {
      await api.writePostCallAction(editing.id, {
        mode: "draft",
        author: "admin",
        commit_msg: `Edit post-call action: ${editing.name}`,
        name: editing.name,
        description: editing.description,
        enabled: editing.enabled,
        order: editing.order,
        prompt: editing.prompt,
      });
      setEditing(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const toggleAction = async (action, enabled) => {
    setBusy(true);
    try {
      await api.writePostCallAction(action.id, {
        mode: "draft",
        author: "admin",
        commit_msg: `${enabled ? "Enable" : "Disable"} ${action.name}`,
        name: action.name,
        description: action.description,
        enabled,
        order: action.order,
        prompt: action.prompt,
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const publishAction = async (actionId) => {
    setBusy(true);
    try {
      await api.publishPostCallAction(actionId);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const deleteAction = async (actionId) => {
    if (!window.confirm(`Delete post-call action "${actionId}"?`)) return;
    setBusy(true);
    try {
      await api.deletePostCallAction(actionId);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Loading />;

  // Editing a single action
  if (editing) {
    return (
      <>
        <PageHeader
          title={editing.name}
          subtitle="Edit post-call analysis action"
          breadcrumbs={[
            { label: "Library", to: `/library/${env}/platform` },
            { label: "Post-Call Actions", to: `/library/${env}/post-call-actions` },
            { label: editing.name },
          ]}
        />
        <div className="page-body">
          <ErrorBanner error={error} />
          <div className="card">
            <div className="agent-config-grid">
              <div className="form-field">
                <label>Name</label>
                <input
                  type="text"
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  readOnly={isLive}
                />
              </div>
              <div className="form-field">
                <label>Action ID</label>
                <input
                  type="text"
                  value={editing.id}
                  readOnly
                  className="mono"
                />
              </div>
              <div className="form-field">
                <label>Order</label>
                <input
                  type="number"
                  min={1}
                  value={editing.order}
                  onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value) || 1 })}
                  readOnly={isLive}
                />
              </div>
            </div>
            <div className="form-field">
              <label>Description</label>
              <textarea
                rows={2}
                value={editing.description}
                onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                readOnly={isLive}
                style={{
                  width: "100%", padding: 8,
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-sm)", fontSize: 13, resize: "vertical",
                }}
              />
            </div>
            <div className="form-field">
              <label>
                Analysis Prompt{" "}
                <span className="muted small">
                  ({(editing.prompt || "").length.toLocaleString()} chars)
                </span>
              </label>
              <textarea
                value={editing.prompt || ""}
                onChange={(e) => setEditing({ ...editing, prompt: e.target.value })}
                readOnly={isLive}
                spellCheck="false"
                style={{
                  width: "100%", minHeight: 400, padding: 12,
                  fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.55,
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-sm)", resize: "vertical",
                  background: "#fafafa",
                }}
              />
            </div>
            <div className="btn-row" style={{ marginTop: 12 }}>
              <button className="btn sm" onClick={() => setEditing(null)}>
                Cancel
              </button>
              {isDraft && (
                <button className="btn primary sm" onClick={saveAction} disabled={busy}>
                  {busy ? "Saving..." : "Save to draft"}
                </button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // List view
  return (
    <>
      <PageHeader
        title="Post-Call Actions"
        subtitle="Analysis checks that run after every call ends. Each action is a separate LLM call."
        breadcrumbs={[
          { label: "Library", to: `/library/${env}/platform` },
          { label: "Post-Call Actions" },
        ]}
        actions={
          isDraft && (
            <button
              className="btn primary sm"
              onClick={() => {
                const id = prompt("Action ID (e.g. custom-check):");
                if (!id) return;
                setEditing({
                  id: id.trim().toLowerCase().replace(/\s+/g, "-"),
                  name: id.trim(),
                  description: "",
                  enabled: true,
                  order: actions.length + 1,
                  prompt: 'Analyze this call and return ONLY valid JSON:\n\n{\n  "your_field": "value"\n}',
                });
              }}
            >
              + New Action
            </button>
          )
        }
      />

      <div className="page-body">
        <ErrorBanner error={error} />

        <p className="muted small mb-16">
          Each action runs as a separate LLM call after every call ends.
          Enable or disable individual checks. Click Edit to customize
          the analysis prompt. When tenants are created, these actions
          are copied to each sub-tenant where they can be further
          customized.
        </p>

        {actions
          .sort((a, b) => (a.order || 99) - (b.order || 99))
          .map((action, idx) => (
            <div
              key={action.id}
              className="card"
              style={{ marginBottom: 12, opacity: action.enabled ? 1 : 0.6 }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <span
                  style={{
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    width: 32, height: 32, borderRadius: "50%",
                    background: action.enabled ? "#6A1B9A" : "#ccc",
                    color: "#fff", fontWeight: 700, fontSize: 14, flexShrink: 0,
                  }}
                >
                  {idx + 1}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{action.name}</div>
                  <div className="muted small mono" style={{ marginTop: 2 }}>
                    {action.id} · Order: {action.order}
                    {action.version && ` · v${action.version}`}
                  </div>
                  <p className="muted small" style={{ marginTop: 6 }}>{action.description}</p>
                  <div className="muted small">
                    Prompt: {(action.prompt || "").length.toLocaleString()} chars
                  </div>
                </div>
                <div className="btn-row" style={{ flexShrink: 0 }}>
                  {isDraft && (
                    <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={action.enabled}
                        onChange={(e) => toggleAction(action, e.target.checked)}
                        disabled={busy}
                      />
                      <span className="small">{action.enabled ? "On" : "Off"}</span>
                    </label>
                  )}
                  <button className="btn sm" onClick={() => setEditing({ ...action })}>
                    {isLive ? "View" : "Edit"}
                  </button>
                  {isDraft && (
                    <>
                      {action.is_draft && !action.is_live && (
                        <button className="btn sm" onClick={() => publishAction(action.id)} disabled={busy}>
                          Publish
                        </button>
                      )}
                      <button
                        className="btn sm"
                        style={{ color: "#c62828" }}
                        onClick={() => deleteAction(action.id)}
                        disabled={busy}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}

        {actions.length === 0 && (
          <div className="empty-state">
            <h3>No post-call actions</h3>
            <p>Click "+ New Action" to create the first post-call analysis check.</p>
          </div>
        )}
      </div>
    </>
  );
}
